import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

/**
 * Single gate for every user-facing entry point into voice calls.
 *
 * Upstream only checks `channel_voice` in a few places, so the sidebar entry,
 * the channel cards and the calls page stay visible even with the feature off.
 * Everything that leads a user into calls goes through this composable instead,
 * which makes the super admin toggle the one switch that turns calls on or off.
 *
 * @returns {{isVoiceCallsVisible: import('vue').ComputedRef<boolean>,
 *            redirectIfVoiceCallsHidden: (routeName?: string) => void}}
 */
export function useVoiceCalls() {
  const router = useRouter();
  const { accountId, currentAccount, isCloudFeatureEnabled } = useAccount();

  const isVoiceCallsVisible = computed(() =>
    Boolean(isCloudFeatureEnabled(FEATURE_FLAGS.CHANNEL_VOICE))
  );

  /**
   * Sends the user away from a calls-only screen reached by a direct link.
   *
   * Account features arrive asynchronously, and until then every flag reads as
   * false — so wait for the account record before deciding, otherwise we would
   * redirect even when the feature is on.
   *
   * @param {string} routeName - Route to land on instead.
   */
  const redirectIfVoiceCallsHidden = (routeName = 'home') => {
    watch(
      () => Boolean(currentAccount.value?.id) && !isVoiceCallsVisible.value,
      isHidden => {
        if (isHidden) {
          router.replace({
            name: routeName,
            params: { accountId: accountId.value },
          });
        }
      },
      { immediate: true }
    );
  };

  return { isVoiceCallsVisible, redirectIfVoiceCallsHidden };
}
